import { PageId } from "../types";
import { TenantAdminCapabilities } from "../hooks/useTenantAdmin";
import type { ProtectedModel } from "./protectedModel";

// ENG-2644 / ENG-2645: the org-admin pages as data — their labels, who may open them, and which
// ones are neighbours.
//
// Extracted so the nav and the per-page "Related" rail agree by construction. They previously
// couldn't: labels lived inline in `buildNavSections`, the Policy capability test was inline beside
// them, and the four cross-links that existed were hand-written per page. Any new admin page had
// three separate places to remember.
//
// Deliberately free of `../styles` and of any component import — `DashboardNav` cannot pull the
// styles module without breaking its render test (see the package CLAUDE.md), and this is on its
// import path.

/**
 * Canonical label for each org-admin page. One source of truth for nav and cross-links.
 *
 * ENG-3479: sentence case, like the rest of the console's copy, and "Managed access" is named for who
 * holds it — an IT provider — rather than for the mechanism.
 */
export const ADMIN_PAGE_LABELS: Partial<Record<PageId, string>> = {
  overview: "Overview",
  users: "Members & seats",
  groups: "Groups",
  // ENG-2687: NOT "Directory". That word is LDAP/AD heritage and says nothing about Google, so an
  // admin looking for where to link their Workspace groups had no reason to open it. The PageId and
  // the `#directory` hash are unchanged on purpose — the backend OAuth callback redirects to
  // `$portal/#directory?connected=1` (PremiumRoutes), so the route name is a backend contract even
  // though the label is not.
  directory: "Google Workspace",
  // ENG-4014: verified by DNS TXT, for any organization, Google Workspace or not.
  domains: "Domains",
  managed: "IT provider access",
  policy: "Policy",
  // ENG-2895: extension policy, inventory and requests are one surface. See
  // docs/EXTENSION_SURFACES_IA.md for why this leaves Policy rather than staying in it.
  extensions: "Extensions",
  report: "Risk report",
  "saas-inventory": "SaaS & AI inventory",
  // ENG-3511: the result of the Protection pages for one member or group, rather than another editor.
  effective: "What a browser gets",
  setup: "Set up Haven",
};

/**
 * ENG-2513: Policy consolidates enforcement, extensions, sites/categories and feature flags, so it
 * shows when the admin holds ANY of those four. Shared rather than re-derived, because a cross-link
 * that disagrees with the nav sends someone to a page that falls back to Home — which reads as a bug.
 *
 * ENG-3059 adds notifications as a fifth section, and therefore to this disjunction. It is the same
 * `isFullTier` value as the other three today, so it changes nobody's nav — but omitting it would
 * leave Policy hidden from an admin whose only remaining capability is the one this predicate does
 * not name, which is the exact failure the ENG-2895 note below describes.
 */
export const canSeePolicy = (c: TenantAdminCapabilities, protectedModel: ProtectedModel | null = 1): boolean =>
  c.canToggleMonitorFirst
  // ENG-4063: under protectedModel 2 the Sites & categories tab is gone (PolicyPage `allSections`), so
  // `canManageLockedSites` opens no tab there. Without this an admin holding it alone (a family whose
  // provider has Policy only access: the language tab needs `writesAll`) got Policy with no tabs.
  // `null` (the model not known yet) reads as 1, as PolicyPage does, so model 1 is unchanged.
  || (c.canManageLockedSites && protectedModel !== 2)
  || c.canManageFeatureFlags || c.canManageNotifications || c.canManageLanguage;

/**
 * ENG-2895: extensions moved OUT of Policy onto their own page, so `canManageWhitelist` moved with
 * them and is deliberately absent from [canSeePolicy] above.
 *
 * Leaving it in both would show Policy to an admin whose only extension capability now lives
 * elsewhere — they would open it and find nothing they can act on. The nav and the cross-links share
 * these predicates for exactly that reason: a cross-link that disagrees with the nav sends someone
 * to a page that falls back to Home, "which reads as a bug".
 */
export const canSeeExtensions = (c: TenantAdminCapabilities): boolean => c.canManageWhitelist;

/**
 * ENG-3526 review: the organization's own pages — every page [canOpenAdminPage] answers for, and
 * nothing else. The one list: the admin-gate notice's pages and the org console's redirect of a page
 * the viewer cannot open (lib/consoleNav) both read it, and a test holds it equal to the switch below,
 * so a new admin page cannot be added to one and not the others.
 */
export const ADMIN_PAGES: readonly PageId[] = [
  "overview", "users", "user", "groups", "directory", "domains", "managed", "policy", "extensions", "effective", "report",
  "saas-inventory", "setup",
];

export const isAdminPage = (page: PageId): boolean => ADMIN_PAGES.includes(page);

/** Whether this admin may open a given org-admin page. */
export function canOpenAdminPage(
  page: PageId,
  c: TenantAdminCapabilities,
  /** ENG-4063: decides Policy only ([canSeePolicy]). */
  protectedModel: ProtectedModel | null = 1,
): boolean {
  switch (page) {
    case "overview":
      return c.canViewDashboard;
    // ENG-3358: `user` is the member detail view — a drill-down from Members & Seats, never a nav
    // entry, opened under the same capability. Listed so this switch is the whole answer to "which
    // pages are the organization's", which the org header reads (showsOrgIdentityHeader).
    case "users":
    case "user":
      return c.canManageMembers;
    case "groups":
      return c.canManageGroups;
    // Directory is group membership by another route. ENG-3749: its own capability since a family has
    // groups but no Workspace directory; for a company it is exactly canManageGroups, as it was.
    case "directory":
      return c.canUseDirectory;
    // ENG-4014: the organization's own owners and admins, never an IT provider (Colin, 2026-10-27):
    // canInviteMembers is the capability no provider tier holds. canUseDirectory keeps it to the plans
    // with a directory; the backend refuses a plan without company enrollment, so a family never adds one.
    case "domains":
      return c.canUseDirectory && c.canInviteMembers;
    // Managed access is granted TO a group (ENG-2390). It is shown to every org admin rather than
    // only to actual MSPs: the "who manages us" half is a security surface, and hiding it until
    // someone claims you would mean the page first appears at the moment it carries bad news.
    // ENG-3407: its own capability, because a provider with Admin access manages groups but may not touch
    // who manages the organization (every /business/msp/* write refuses a provider).
    case "managed":
      return c.canManageManagedAccess;
    case "policy":
      return canSeePolicy(c, protectedModel);
    case "extensions":
      return canSeeExtensions(c);
    // ENG-3511: it reads sites, extensions, the mode and the flags. ENG-3407: and changes nothing, so
    // it is open to every console role of a full-tier organization, a Read-only provider included —
    // for whom it is the organization's policy, where Policy and Extensions are editors they cannot use.
    case "effective":
      return c.canViewProtection;
    case "report":
      return c.canViewRiskReport;
    case "saas-inventory":
      return c.canViewSaasInventory;
    // ENG-3457: the organization's own admins, family included. canInviteMembers is the one capability
    // no provider tier holds, and setting up an organization is never a provider's (ENG-3532).
    case "setup":
      return c.canInviteMembers;
    default:
      return false;
  }
}

/**
 * ENG-3358: whether an admin sees the organization header (its name, type and enforcement mode) above
 * [page]. ENG-3479 moved the switcher out of it, into the nav.
 *
 * On every org-admin page they can open, so it follows [canOpenAdminPage] instead of keeping a list
 * of its own. It was a hand-kept list in DashboardLayout that never gained Google Workspace, Managed
 * access or the member detail view — where an admin disconnects a directory, approves a provider or
 * deactivates someone, which is when the header exists to say which organization they are in
 * (OrgIdentityHeader, ENG-2494). A page they cannot open falls back to Home, so it gets no header.
 *
 * Plus Billing (ENG-3479): the organization's plan, seats and billing, which ENG-2690 put the header
 * over when they were still on Settings. No capability test there: Billing stays open to an admin
 * whose licence lapsed, who has no capabilities at all, and the caller decides who reaches it.
 *
 * Settings is personal now, so it has no header. Neither has Protected sites: its changes land on the
 * organization that governs this browser, which is not always the one the header would name (ENG-3469
 * — the page names that organization itself).
 */
export const showsOrgIdentityHeader = (page: PageId, c: TenantAdminCapabilities, protectedModel: ProtectedModel | null = 1): boolean =>
  page === "billing" || canOpenAdminPage(page, c, protectedModel);

/**
 * Which pages each admin surface should point at.
 *
 * Every link that existed before this was one-directional — Policy→Groups, Overview→Policy,
 * Groups→(Directory · Policy · member), Directory→Members — with **no return path anywhere**. An
 * admin who followed one had to find their own way back through the sidebar, and an admin who landed
 * cold got no hint that the answer lived one tab over. That is mechanically how a missing sync step
 * read as a broken integration on 2026-07-30: Directory offered no route back to Groups, so nothing
 * joined the two halves of one task.
 *
 * The table is symmetric on purpose — if A lists B, B lists A.
 */
export const RELATED_PAGES: Partial<Record<PageId, PageId[]>> = {
  users: ["groups", "directory", "domains", "overview", "managed", "setup"],
  // ENG-2390: a grant is held by a group, so the two pages answer halves of one question —
  // "who is in this group" and "what does that group reach".
  groups: ["directory", "policy", "users", "managed", "effective"],
  directory: ["groups", "users", "domains", "setup"],
  // ENG-4014: the other way to verify a domain, the members it brings in, and the checklist step.
  domains: ["directory", "users", "setup"],
  managed: ["groups", "users"],
  // ENG-3164: SaaS/AI Inventory holds the switch that decides whether Policy's Tracked column does
  // anything, so the two pages answer halves of one question and each now points at the other.
  policy: ["extensions", "groups", "overview", "report", "saas-inventory", "effective"],
  // Extensions leads to Overview (where the inventory still lives until ENG-2892) and back to the
  // rest of policy.
  extensions: ["overview", "policy", "report", "effective"],
  overview: ["users", "report", "policy", "extensions", "setup"],
  report: ["overview", "policy", "extensions", "saas-inventory"],
  "saas-inventory": ["report", "policy"],
  // ENG-3511: the three pages whose settings it shows the result of.
  effective: ["policy", "extensions", "groups"],
  // ENG-3457: the pages its steps send an admin to, and the way back to it once Overview's card is hidden.
  setup: ["overview", "users", "directory", "domains"],
};

export interface RelatedPage {
  id: PageId;
  label: string;
}

/**
 * The neighbours of [page] this admin can actually open.
 *
 * Gated on the capability owning the TARGET, never the source: a link to a page the admin lacks
 * falls back to Home (`resolveHashToPage`), which looks like a broken link rather than a missing
 * permission. Returns an empty list rather than a partial rail when nothing qualifies.
 */
export function relatedPages(
  page: PageId,
  capabilities: TenantAdminCapabilities,
  /** ENG-4063: under protectedModel 2 "What a browser gets" is retired, so no rail links to it. */
  protectedModel: 1 | 2 | null = 1,
): RelatedPage[] {
  return (RELATED_PAGES[page] ?? [])
    .filter((target) => !(protectedModel === 2 && target === "effective"))
    .filter((target) => canOpenAdminPage(target, capabilities, protectedModel))
    .map((target) => ({ id: target, label: ADMIN_PAGE_LABELS[target] ?? target }));
}
